import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
	output
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { AuthService } from '@core/services/auth.service';
import { ConstantsService } from '@core/services/constants.service';
import { SharedModule } from '@shared/shared.module';
import { isEqual } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { SettingsService } from '@pages/training/settings/settings.service';

@Component({
	selector: 'app-smtp-configuration',
	imports: [
		CommonModule,
		MaterialModule,
		FormsModule,
		ReactiveFormsModule,
		SharedModule
	],
	templateUrl: './smtp-configuration.component.html',
	styleUrl: './smtp-configuration.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmtpConfigurationComponent implements OnInit {
	smtpForm!: FormGroup;

	formStatus = output<string>();
	mailSettings: any = {};
	initialFormValues: any; // keep track of any changes

	private readonly authService: AuthService = inject(AuthService);
	private readonly constantService: ConstantsService =
		inject(ConstantsService);
	private readonly settingsService: SettingsService = inject(SettingsService);
	clientToken: any;
	userToken: any;

	constructor(
		private readonly fb: FormBuilder,
		private readonly toastr: ToastrService,
		private readonly cdr: ChangeDetectorRef
	) {
		this.clientToken = this.constantService.CLIENT_TOKEN;
		this.userToken = this.authService.getToken();

		this.smtpForm = this.fb.group({
			MailSettingsNumber: [0],
			from: ['', [Validators.required, Validators.email]],
			subject: [''], // Hidden field, optional
			host: ['', Validators.required],
			port: ['', Validators.required],
			user_name: ['', Validators.required],
			password: ['', Validators.required],
			ssl: [false, Validators.required]
		});

		// Listen to form changes to check if it's touched or dirty
		this.smtpForm.valueChanges.subscribe(() => {
			this.checkFormStatus();
		});
	}

	ngOnInit(): void {
		this.getMailSettings();

		// Save the initial form values when the form is initialized
		this.initialFormValues = this.smtpForm.value;

		// Subscribe to form value changes to monitor if the form reverts back to its original state
		this.smtpForm.valueChanges.subscribe(() => {
			this.checkFormState();
		});
	}

	checkFormState() {
		// Compare current form values with the initial values
		const currentValues = this.smtpForm.value;

		if (isEqual(currentValues, this.initialFormValues)) {
			// If the values match, mark the form as pristine and untouched
			this.smtpForm.markAsPristine();
			this.smtpForm.markAsUntouched();
		}
	}

	getMailSettings() {
		this.settingsService.getMailSettings().subscribe({
			next: (response) => {
				this.mailSettings = response;
				if (this.mailSettings) {
					this.patchValues();
				}
				// this.cdr.detectChanges();
			},
			error: (error) => {
				console.log('getMailSettings error : ', error);
			}
		});
	}

	patchValues() {
		//bind data
		this.smtpForm.patchValue({
			MailSettingsNumber: this.mailSettings.MailSettingsNumber || '',
			from: this.mailSettings.SMTPFrom || '',
			subject: this.mailSettings.SMTPSubject || '',
			host: this.mailSettings.SMTPHost || '',
			port: this.mailSettings.SMTPPort || '',
			user_name: this.mailSettings.SMTPUserName || '',
			password: this.mailSettings.SMTPPassword || '',
			ssl: this.mailSettings.SMTPEnableSsl || false
		});

		// Update initial form values after patching
		this.initialFormValues = this.smtpForm.value;
	}

	checkFormStatus() {
		const isFormDirty: any = this.smtpForm.dirty || this.smtpForm.touched;
		this.formStatus.emit(isFormDirty);
	}

	onCheckboxChange(controlName: string, event: any): void {
		const checked = event.target.checked;

		// Get the current form control
		const control = this.smtpForm.get(controlName);
		if (control) {
			// Update the form control value directly
			control.setValue(checked);
			control.markAsDirty();
			control.updateValueAndValidity();

			// Also update the form state
			this.smtpForm.markAsDirty();
			this.smtpForm.updateValueAndValidity();
		}
	}

	saveSmtpConfiguration() {
		if (this.smtpForm.valid) {
			const formData = this.smtpForm.value;
			const payload = {
				ClientToken: this.clientToken,
				MailSettingsNumber: formData?.MailSettingsNumber,
				SMTPEnableSsl: formData?.ssl,
				SMTPFrom: formData?.from,
				SMTPHost: formData?.host,
				SMTPPassword: formData?.password,
				SMTPPort: formData?.port,
				SMTPSubject: formData?.subject,
				SMTPUserName: formData?.user_name,
				UserToken: this.userToken
			};

			//save mail settings
			this.settingsService.saveMailSetting(payload).subscribe({
				next: (response) => {
					if (response === true) {
						this.toastr.clear();
						this.toastr.success(
							'SMTP Configuration UPDATED successfully.',
							'Success',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-success'
							}
						);

						// Mark form as pristine after successful save
						this.smtpForm.markAsPristine();
						this.initialFormValues = this.smtpForm.value;
						this.checkFormStatus();
					} else {
						this.toastr.clear();
						this.toastr.error('Something went wrong', 'Error', {
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-error'
						});
					}
					this.cdr.detectChanges();
				},
				error: (error) => {
					console.log('saveMailSetting error : ', error);
					this.toastr.error(
						'Failed to save SMTP configuration',
						'Error',
						{
							positionClass: 'toast-bottom-left',
							toastClass: 'toast-icon custom-toast-error'
						}
					);
					this.cdr.detectChanges();
				}
			});
		} else {
			// Mark all fields as touched to show validation errors
			Object.keys(this.smtpForm.controls).forEach((key) => {
				this.smtpForm.get(key)?.markAsTouched();
			});
			this.toastr.error(
				'Please fill all required fields',
				'Validation Error',
				{
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				}
			);
			this.cdr.detectChanges();
		}
	}
}
